import { Link, Sheet } from '@cannect/new-components/atoms'
import React, { useState } from 'react'
import {
  LuChevronLeft,
  LuChevronRight,
  LuFileText,
  LuHome,
  LuKey,
  LuLogOut,
  LuMapPin,
  LuMenu,
  LuPhone,
  LuShoppingBag,
  LuShoppingCart,
  LuUser
} from 'react-icons/lu'
import { useHistory } from 'react-router-dom'
import { useHeader } from './useHeader'

type MobileMenuSheetProps = {
  isOpen: boolean
  onClose: () => void
  menuConfig: ReturnType<typeof useHeader>['menuConfig']
  signedUserName: string
  isLogged: boolean
  handleLogout: () => void
}

type NavigationLevel = 'main' | 'perfil'

// Menu items para área de associados
const ASSOCIATION_MENU_ITEMS = [
  {
    id: 'inicio',
    title: 'Início',
    path: '/AreaAssociados/MinhaConta?pagina=inicio',
    icon: LuHome
  },
  {
    id: 'perfil',
    title: 'Meu perfil',
    icon: LuUser,
    hasSubmenu: true,
    subItems: [
      {
        title: 'Dados pessoais',
        path: '/AreaAssociados/MinhaConta?pagina=perfil-dados-pessoais',
        icon: LuUser
      },
      {
        title: 'Alterar senha',
        path: '/AreaAssociados/MinhaConta?pagina=perfil-alterar-senha',
        icon: LuKey
      },
      {
        title: 'Documentos',
        path: '/AreaAssociados/MinhaConta?pagina=perfil-documentos',
        icon: LuFileText
      },
      {
        title: 'Endereços',
        path: '/AreaAssociados/MinhaConta?pagina=perfil-enderecos',
        icon: LuMapPin
      }
    ]
  },
  {
    id: 'pedidos',
    title: 'Meus pedidos',
    path: '/AreaAssociados/MinhaConta?pagina=pedidos-todos',
    icon: LuShoppingBag
  },
  {
    id: 'carrinho',
    title: 'Carrinho',
    path: '/AreaAssociados/MinhaConta?pagina=carrinho',
    icon: LuShoppingCart
  }
]

export const MobileMenuSheet = ({
  isOpen,
  onClose,
  menuConfig: _menuConfig,
  signedUserName,
  isLogged,
  handleLogout
}: MobileMenuSheetProps) => {
  const [currentLevel, setCurrentLevel] = useState<NavigationLevel>('main')
  const [currentSubmenu, setCurrentSubmenu] = useState<(typeof ASSOCIATION_MENU_ITEMS)[0] | null>(null)
  const history = useHistory()

  const handleClose = () => {
    setCurrentLevel('main')
    setCurrentSubmenu(null)
    onClose()
  }

  const handleRedirect = (path: string) => {
    history.push(path)
    handleClose()
  }

  const handleMenuItemClick = (item: (typeof ASSOCIATION_MENU_ITEMS)[0], event: React.MouseEvent) => {
    if (item.hasSubmenu && item.subItems) {
      event.preventDefault()
      setCurrentSubmenu(item)
      setCurrentLevel('perfil')
    } else if (item.path) {
      handleRedirect(item.path)
    }
  }

  const handleBack = () => {
    setCurrentLevel('main')
    setCurrentSubmenu(null)
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={handleClose}
          style={{ backdropFilter: 'blur(4px)' }}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClose()
            }
          }}
        />
      )}

      <Sheet.Root open={isOpen} onOpenChange={handleClose}>
        <Sheet.Content
          side="right"
          className="z-50 w-[320px] min-w-fit border-0 bg-white p-0 text-neutral-700 [&>button]:hidden"
          hideOverlay>
          <div className="flex h-full w-full flex-col bg-white">
            <div className="flex items-center justify-between border-b border-neutral-200 p-6">
              <div className="flex items-center gap-2">
                <LuMenu className="h-5 w-5 text-[#02B0BB]" />
                <span className="text-lg font-medium text-[#02B0BB]">Menu</span>
              </div>
              <button
                onClick={handleClose}
                className="rounded border-none bg-transparent p-1 transition-colors hover:bg-neutral-100">
                <span className="text-xl text-neutral-500">×</span>
              </button>
            </div>

            <div className="border-b border-neutral-200 px-6 py-4">
              {isLogged ? (
                <div className="flex items-center gap-3">
                  <LuUser className="h-5 w-5 text-[#02B0BB]" />
                  <span className="text-sm font-medium text-[#02B0BB]">Olá, {signedUserName}</span>
                </div>
              ) : (
                <Link
                  to="/AreaAssociados"
                  onClick={onClose}
                  className="flex items-center gap-3 text-[#02B0BB] hover:text-[#02B0BB]">
                  <LuUser className="h-5 w-5" />
                  <span className="text-sm font-medium underline">Fazer Login</span>
                </Link>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {currentLevel !== 'main' && (
                <div className="border-b border-neutral-200 px-6 py-4">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 border-none bg-transparent p-0 text-neutral-600 transition-colors hover:text-[#02B0BB]">
                    <LuChevronLeft className="h-4 w-4" />
                    <span className="text-sm font-medium">Voltar</span>
                  </button>
                </div>
              )}

              <nav className="py-2">
                {currentLevel === 'main' && (
                  <>
                    {ASSOCIATION_MENU_ITEMS.map((item) => {
                      const IconComponent = item.icon
                      return (
                        <div key={item.id}>
                          <Link
                            to={item.path || '#'}
                            onClick={(e) => handleMenuItemClick(item, e)}
                            className="flex items-center justify-between px-6 py-4 text-neutral-700 transition-colors hover:bg-neutral-50">
                            <div className="flex items-center gap-3">
                              <IconComponent className="h-5 w-5 text-[#02B0BB]" />
                              <span className="text-base font-medium">{item.title}</span>
                            </div>
                            {item.hasSubmenu && <LuChevronRight className="h-4 w-4 text-neutral-400" />}
                          </Link>
                        </div>
                      )
                    })}
                  </>
                )}

                {currentLevel === 'perfil' && currentSubmenu && (
                  <>
                    <div className="px-6 py-2">
                      <span className="text-sm font-medium uppercase tracking-wide text-neutral-400">
                        {currentSubmenu.title}
                      </span>
                    </div>
                    {currentSubmenu.subItems?.map((subItem) => {
                      const SubIconComponent = subItem.icon
                      return (
                        <Link
                          key={subItem.title}
                          to={subItem.path}
                          onClick={() => handleRedirect(subItem.path)}
                          className="flex items-center gap-3 px-6 py-4 text-neutral-700 transition-colors hover:bg-neutral-50">
                          <SubIconComponent className="h-5 w-5 text-[#02B0BB]" />
                          <span className="text-base font-medium">{subItem.title}</span>
                        </Link>
                      )
                    })}
                  </>
                )}
              </nav>
            </div>

            <div className="space-y-4 border-t border-neutral-200 p-6">
              {isLogged && (
                <button
                  onClick={() => {
                    handleLogout()
                    handleClose()
                  }}
                  className="flex w-full items-center gap-3 border-none bg-transparent p-0 text-left text-neutral-700 hover:text-[#02B0BB]">
                  <LuLogOut className="h-5 w-5" />
                  <span className="text-sm font-medium underline">Sair</span>
                </button>
              )}

              <a href="tel:+5511982711992" className="flex items-center gap-3 text-neutral-700 hover:text-[#02B0BB]">
                <LuPhone className="h-5 w-5" />
                <span className="text-sm font-medium underline">75 9999-00420</span>
              </a>
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Root>
    </>
  )
}
